// Geo → payment method derivation (payment-capture design §1.1, §3.2, D1).
//
// THE METHOD IS DERIVED, NOT CHOSEN. A US billing country means ACH; anywhere
// else means credit card. The customer is never offered the other one, so this
// module has no notion of a "selected" method and deliberately exposes no way
// to override its answer — a picker that can contradict the geo rule is the
// specific defect §3.2 exists to prevent.
//
// WHY ACH IS NOT A POLICY MAPPING. ACH is a US-domestic rail: ABA routing
// numbers exist only in the United States. "US billing address ⇒ ACH" is what
// the rail permits, not a preference someone chose (§1.2).
//
// THIS IS A PROJECTION, NEVER THE GUARD (§1.3). The authoritative evaluation
// happens server-side at order creation against the country snapshotted onto
// the order. This copy exists so the customer is not shown a form they cannot
// use; a client that lied about its method would still be rejected there.

/** The two methods D1 admits. There is deliberately no third value. */
export type DerivedPaymentMethod = "ach" | "card";

/** The geo authority's domestic value. */
const DOMESTIC_COUNTRY_CODE = "US";

/** Exactly two ASCII letters — the shape §1.6 requires of a country code. */
const ISO_ALPHA2 = /^[A-Za-z]{2}$/;

/**
 * The payment method for a billing country, or `null` when the country cannot
 * be resolved.
 *
 * `null` is NOT "fall back to ACH". §1.6 is explicit that there is no safe
 * default: defaulting an unresolvable country to US wrongly enables ACH for an
 * international buyer, and defaulting it to card wrongly denies a domestic one.
 * Callers must treat `null` as "cannot take payment yet", never as a value.
 *
 * A code that is not exactly two ASCII letters is a rejection, not a coercion
 * (§1.6) — so `"USA"`, `"12"` and `""` all resolve to `null` rather than being
 * trimmed, padded or pattern-matched into `US`.
 */
export function derivePaymentMethod(
  countryCode: string | null | undefined,
): DerivedPaymentMethod | null {
  const raw = (countryCode ?? "").trim();
  if (!ISO_ALPHA2.test(raw)) return null;
  return raw.toUpperCase() === DOMESTIC_COUNTRY_CODE ? "ach" : "card";
}

/**
 * The short line shown under the stated method.
 *
 * F23 changed what this is: the bank-transfer line used to JUSTIFY the routing
 * ("Orders billed to a United States address are paid by bank transfer…") and
 * now INSTRUCTS instead — "Use a business checking account." The screen states
 * what to pay with and does not argue for it, so the geography and the rail's
 * acronym stay out of the customer-visible copy.
 *
 * ⚠️ AMENDED 2026-10-10 (merge-review M1 D10; operator answer: "checkout card
 * line don't mention geography neither"). ORIGINAL (verbatim): "The card line
 * keeps its geography, because an international customer genuinely is being
 * told why they see a card form." CURRENT: the card line is an instruction
 * too — "Pay by credit card. We accept Visa and Mastercard." — matching the
 * Go 422 strings, which name the networks and no geography.
 *
 * This is interface chrome, NOT the payment terms: terms text is
 * operator-configured and arrives from the server (§4, D5). Nothing here is a
 * legal statement and nothing here may become one.
 */
export function paymentMethodReason(method: DerivedPaymentMethod): string {
  return method === "ach"
    ? "Use a business checking account."
    : "Pay by credit card. We accept Visa and Mastercard.";
}

/** Human label for the derived method, used in headings and summaries. */
export function paymentMethodLabel(method: DerivedPaymentMethod): string {
  // F23: "Bank transfer" — the rail's acronym never reaches the heading.
  // ("ach" itself is the server's wire value for the method, not a label.)
  return method === "ach" ? "Bank transfer" : "Credit card";
}
